import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import styles from './felipebeloopersonal_consultoria.module.scss';
import WhatsappIcon from '../../icons/whatsappIcon.tsx';

const WHATSAPP_NUMBER = '5517991396467';
const WHATSAPP_MSG_CONSULTORIA =
    'Olá! Vi os planos de consultoria online no site e quero tirar minhas dúvidas antes de assinar. Podemos conversar?';

const whatsappConsultoriaLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MSG_CONSULTORIA)}`;

type PlanTier = 'bronze' | 'gold' | 'black';

type Plan = {
    tier: PlanTier;
    tierLabel: string;
    priceLabel: string;
    body: string[];
    listLead: string;
    bullets: string[];
    footer?: string;
    checkoutUrl: string;
    ctaLabel: string;
};

const PLANS: Plan[] = [
    {
        tier: 'bronze',
        tierLabel: 'Plano Bronze',
        priceLabel: 'R$130,00/mês',
        body: [
            'Ideal para quem quer começar agora e sentir na prática como funciona o Método FB.',
            'Nesse plano, você recebe um acompanhamento totalmente personalizado, com treino ajustado à sua rotina, objetivo e nível de experiência.',
        ],
        listLead: 'Perfeito para quem deseja:',
        bullets: [
            'Criar consistência nos treinos',
            'Aprender execução correta dos exercícios',
            'Ter acompanhamento profissional',
            'Evoluir sem treinar no automático',
        ],
        checkoutUrl: 'https://pages.mfitpersonal.com.br/p/2l28?checkout=true',
        ctaLabel: 'Quero o Bronze',
    },
    {
        tier: 'gold',
        tierLabel: 'Plano Gold',
        priceLabel: 'R$290,00/trimestral',
        body: [
            'O plano ideal para quem quer evolução contínua e resultados mais visíveis.',
            'Em 3 meses, conseguimos aplicar progressões, ajustes estratégicos e acompanhar de perto sua evolução física e desempenho nos treinos.',
        ],
        listLead: 'Nesse período, você terá:',
        bullets: [
            'Treinos personalizados e atualizados',
            'Estratégias de progressão de carga',
            'Correções e acompanhamento constante',
            'Mais tempo para construir disciplina e resultado',
        ],
        checkoutUrl: 'https://pages.mfitpersonal.com.br/p/2lca?checkout=true',
        ctaLabel: 'Quero o Gold',
    },
    {
        tier: 'black',
        tierLabel: 'Plano Black',
        priceLabel: 'R$550,00/semestral',
        body: [
            'O acompanhamento mais completo da consultoria.',
            'O foco aqui é transformação real: evolução física, constância e construção de resultados duradouros.',
        ],
        listLead: 'Ao longo de 6 meses, conseguimos trabalhar:',
        bullets: [
            'Evolução progressiva e estruturada',
            'Mudanças físicas mais expressivas',
            'Adaptação contínua dos treinos',
            'Desenvolvimento de força, estética e performance',
            'Construção de hábitos sólidos',
        ],
        footer:
            'Esse é o plano para quem decidiu levar o processo a sério e quer fazer parte do Team FB com foco total na evolução.',
        checkoutUrl: 'https://pages.mfitpersonal.com.br/p/qan?checkout=true',
        ctaLabel: 'Quero o Black',
    },
];

const cardClassByTier: Record<PlanTier, string> = {
    bronze: styles.planCardBronze,
    gold: styles.planCardGold,
    black: styles.planCardBlack,
};

const tierTitleClassByTier: Record<PlanTier, string> = {
    bronze: styles.planTierBronze,
    gold: styles.planTierGold,
    black: styles.planTierBlack,
};

const Felipebeloopersonal_consultoria: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className={styles.felipebeloopersonal_consultoria}>
            <div className={styles.page}>
                <div className={styles.header}>
                    <Link
                        to="/felipebeloopersonal"
                        state={{ skipLoader: true }}
                        className={styles.backButton}
                        aria-label="Voltar ao cartão digital"
                    >
                        <FaArrowLeft />
                    </Link>
                    <div className={styles.headerText}>
                        <p className={styles.eyebrow}>Felipe Belo</p>
                        <h1 className={styles.title}>Consultoria</h1>
                    </div>
                </div>

                <p className={styles.intro}>
                    Escolha o ritmo que combina com o seu momento. Todos os planos incluem o Método FB com treinos sob medida,
                    acompanhamento profissional e foco em resultado, você decide por quanto tempo quer se comprometer com a sua
                    melhor versão.
                </p>

                <div className={styles.plans}>
                    {PLANS.map((plan) => (
                        <article
                            key={plan.tier}
                            className={`${styles.planCard} ${cardClassByTier[plan.tier]}`}
                            aria-labelledby={`plan-${plan.tier}-title`}
                        >
                            <div className={styles.planHeader}>
                                <h2 id={`plan-${plan.tier}-title`} className={`${styles.planTier} ${tierTitleClassByTier[plan.tier]}`}>
                                    {plan.tierLabel}
                                </h2>
                                <p className={styles.planPrice}>{plan.priceLabel}</p>
                            </div>
                            {plan.body.map((paragraph, i) => (
                                <p
                                    key={i}
                                    className={`${styles.planBody} ${i === plan.body.length - 1 ? styles.planBodyLast : ''}`}
                                >
                                    {paragraph}
                                </p>
                            ))}
                            <p className={styles.listLead}>{plan.listLead}</p>
                            <ul className={styles.bulletList}>
                                {plan.bullets.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                            {plan.footer ? <p className={styles.planFooter}>{plan.footer}</p> : null}
                            <a
                                href={plan.checkoutUrl}
                                className={styles.checkoutButton}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {plan.ctaLabel}
                            </a>
                        </article>
                    ))}
                </div>

                <p className={styles.trustNote}>
                    Pagamento e assinatura são processados na plataforma parceira em ambiente seguro. Em caso de dúvidas sobre
                    qual plano escolher, fale com a equipe pelo WhatsApp.
                </p>

                <div className={styles.ctaSection} aria-labelledby="cta-consultoria-heading">
                    <h2 id="cta-consultoria-heading" className={styles.ctaHeading}>
                        Ainda com dúvida sobre qual plano é o seu?
                    </h2>
                    <p className={styles.ctaText}>
                        Conte seu objetivo e sua rotina: ajudamos você a escolher a opção que entrega o melhor custo-benefício
                        para o seu momento, sem pressão e com transparência.
                    </p>
                    <a
                        href={whatsappConsultoriaLink}
                        className={styles.ctaWhatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <WhatsappIcon height="22" color="#ffffff" />
                        <span>Falar no WhatsApp agora</span>
                    </a>
                </div>
            </div>
        </div>
    );
};

export default Felipebeloopersonal_consultoria;
